// ============================================================
// VELLA DESIGN SYSTEM — design tokens
// Ocean blue over warm-neutral grays. Low-chroma, never electric.
// ------------------------------------------------------------
// `DS` is a plain JS object for inline-style use (matches the
// app's existing inline-style pattern). The matching :root
// CSS-variable block, font import, base reset, and interaction
// utilities live in `design-system.css` (imported from index.js).
// This is a visual layer only — no logic, no API, no state.
// ============================================================

// ─── Blue scale (deep, slightly teal ocean — low chroma) ─────
export const BLUE = {
  50:  "#eef4f7",
  100: "#dbe9ee",
  200: "#bcd3dc",
  300: "#93b6c4",
  400: "#5f93a6",
  500: "#3d7488",
  600: "#2f6478",
  700: "#275c73", // primary
  800: "#224a5c",
  900: "#1f3e4c",
  950: "#142932",
};

// ─── Slate scale (near-neutral grays, a whisper of cool) ─────
export const SLATE = {
  50:  "#f7f9fa",
  100: "#eef2f4",
  200: "#e1e7ea",
  300: "#cbd4d9",
  400: "#9aa7ae",
  500: "#6c7880",
  600: "#515b63",
  700: "#3c454b",
  800: "#2a3137",
  900: "#1a1f23",
};

// ─── Sea-glass / sky accents ─────────────────────────────────
export const ACCENT = {
  seaglass: "#6fb7c4",
  sky:      "#a9d2dc",
  mist:     "#d7e7ec",
};

// ─── Status (muted, never neon — sits in the ocean palette) ──
export const STATUS = {
  green: "#3f8a63", // success
  amber: "#c89243", // warning
  red:   "#c95c52", // danger / destructive
};

// ─── Background surfaces ─────────────────────────────────────
export const SURFACE = {
  app:      "#f4f7f9", // canvas
  panel:    "#eaf0f3", // sidebars
  elevated: "#ffffff", // cards
};

// ─── Cool-tinted shadow scale (5 steps, low spread) ──────────
const T = "39,92,115"; // ocean #275c73 as rgb
export const SHADOW = {
  1: `0 1px 2px rgba(${T},0.06)`,
  2: `0 1px 3px rgba(${T},0.08), 0 1px 2px rgba(${T},0.05)`,
  3: `0 4px 12px -2px rgba(${T},0.10), 0 2px 6px -2px rgba(${T},0.07)`,
  4: `0 12px 28px -8px rgba(${T},0.16), 0 6px 14px -6px rgba(${T},0.10)`,
  5: `0 24px 56px -16px rgba(${T},0.22), 0 10px 24px -10px rgba(${T},0.12)`,
};

// ─── Corner radii ────────────────────────────────────────────
export const RADIUS = {
  md:   10, // controls
  lg:   14,
  xl:   18, // cards
  "2xl": 24, // bubbles / sheets
  pill: 999,
};

// ─── Animation ───────────────────────────────────────────────
export const MOTION = {
  fast: "120ms",
  base: "200ms",
  slow: "320ms",
  easeOut: "cubic-bezier(0.22, 1, 0.36, 1)",
};

// ─── Typography ──────────────────────────────────────────────
export const FONT_SANS = `"Inter", -apple-system, BlinkMacSystemFont, "SF Pro Text", system-ui, "Helvetica Neue", sans-serif`;
export const FONT_MONO = `"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace`;

export const TYPE = {
  body:    { fontSize: 16, lineHeight: 1.55, fontWeight: 450 },
  display: { letterSpacing: "-0.02em", fontWeight: 700 },
  eyebrow: { fontSize: 12, letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 600 },
};

// ─── Bundled token object (convenience for inline styles) ────
export const DS = {
  blue: BLUE,
  slate: SLATE,
  accent: ACCENT,
  status: STATUS,
  surface: SURFACE,
  shadow: SHADOW,
  radius: RADIUS,
  motion: MOTION,
  type: TYPE,
  fontSans: FONT_SANS,
  fontMono: FONT_MONO,
  primary: BLUE[700],
  ring: `0 0 0 3px rgba(${T},0.35)`,
};

// The matching CSS layer (:root custom properties, font import, base
// reset, interaction utilities, and orb/loader keyframes) now lives in
// `design-system.css`, imported once from `index.js`. Components may
// reference either the `DS` JS object (inline) or the var(--…) tokens.
